/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Minimal CBOR (RFC 8949) decoder, sufficient for C2PA claims, assertions and
 * COSE structures. From scratch — no cbor-x dependency.
 *
 * Decisions tuned for C2PA fidelity:
 * - byte strings decode to `Uint8Array` (claims carry raw hashes), text strings to `string`.
 * - maps decode to a plain object when *every* key is a text string (claims,
 *   assertions, crJSON-shaped data); otherwise to a `Map` (COSE headers use
 *   integer keys). See {@link CborMap}.
 * - tags decode to {@link CborTagged} `{ tag, value }` so callers can recognize
 *   e.g. COSE_Sign1 (tag 18) and CBOR-encoded dates.
 * - 64-bit integers that exceed `Number.MAX_SAFE_INTEGER` decode to `bigint`.
 */

export type CborMap = Record<string, unknown> | Map<unknown, unknown>

/** A CBOR tagged value (major type 6). */
export class CborTagged {
    constructor (public readonly tag: number, public readonly value: unknown) {}
}

/** Break marker for indefinite-length items (major type 7, value 31). */
const BREAK = Symbol('cbor-break')

/**
 * Maximum nesting depth. C2PA claims/assertions/COSE are shallow (a handful of
 * levels); this bounds adversarial deeply-nested input so `readItem` recursion
 * cannot exhaust the stack (RangeError) on a crafted array/map/tag bomb.
 */
const MAX_DEPTH = 1024

class Cursor {
    pos = 0
    depth = 0
    readonly dv: DataView
    constructor (public readonly u8: Uint8Array) {
        this.dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength)
    }

    byte (): number {
        if (this.pos >= this.u8.length) throw new CborError('unexpected end of input')
        return this.u8[this.pos++]
    }

    bytes (n: number): Uint8Array {
        if (this.pos + n > this.u8.length) throw new CborError('unexpected end of input')
        const out = this.u8.subarray(this.pos, this.pos + n)
        this.pos += n
        return out
    }

    uint (n: number): number {
        let v = 0
        for (let i = 0; i < n; i++) v = v * 256 + this.byte()
        return v
    }
}

export class CborError extends Error {}

/** Decodes the single CBOR item filling `bytes`. Throws on trailing bytes. */
export function decode (bytes: Uint8Array): unknown {
    const c = new Cursor(bytes)
    const value = readItem(c)
    // Well-formedness (RFC 8949 App. C / §15.10.3.1): a single-item decode must
    // consume the whole input. Trailing bytes indicate a malformed/ambiguous stream.
    if (c.pos !== bytes.length) throw new CborError('trailing bytes after CBOR item')
    return value
}

/** Decodes the first CBOR item starting at `offset`; returns it and the next offset. */
export function decodeFirst (bytes: Uint8Array, offset = 0): { value: unknown, offset: number } {
    const c = new Cursor(bytes)
    c.pos = offset
    const value = readItem(c)
    return { value, offset: c.pos }
}

function readItem (c: Cursor): unknown {
    if (++c.depth > MAX_DEPTH) throw new CborError('nesting too deep')
    try {
        const ib = c.byte()
        const major = ib >> 5
        const minor = ib & 0x1f

        switch (major) {
            case 0: return readUint(c, minor) // unsigned int
            case 1: { // negative int
                const u = readUint(c, minor)
                return typeof u === 'bigint' ? -1n - u : -1 - u
            }
            case 2: return readByteString(c, minor) // byte string
            case 3: return readTextString(c, minor) // text string
            case 4: return readArray(c, minor) // array
            case 5: return readMap(c, minor) // map
            case 6: { // tag
                const tag = readUint(c, minor)
                const value = readItem(c)
                return new CborTagged(Number(tag), value)
            }
            case 7: return readSimple(c, minor) // simple/float
            default: throw new CborError(`bad major type ${major}`)
        }
    } finally {
        c.depth--
    }
}

/** Reads the argument of an initial byte; returns the value or a marker for indefinite. */
function readUint (c: Cursor, minor: number): number | bigint {
    if (minor < 24) return minor
    if (minor === 24) return c.byte()
    if (minor === 25) return c.uint(2)
    if (minor === 26) return c.uint(4)
    if (minor === 27) {
        const hi = c.uint(4)
        const lo = c.uint(4)
        const big = (BigInt(hi) << 32n) | BigInt(lo)
        return big <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(big) : big
    }
    throw new CborError(`bad additional info ${minor}`)
}

function isIndefinite (minor: number): boolean {
    return minor === 31
}

function readByteString (c: Cursor, minor: number): Uint8Array {
    if (isIndefinite(minor)) {
        const parts: Uint8Array[] = []
        for (;;) {
            const ib = c.byte()
            if (ib === 0xff) break
            // RFC 8949 §3.2.3: the chunks of an indefinite-length string must
            // themselves be definite-length. Rejecting a nested indefinite marker
            // (minor 31) both enforces well-formedness and stops the per-chunk
            // self-recursion from a run of 0x5f bytes exhausting the stack —
            // `readByteString` never increments `c.depth`, so MAX_DEPTH would not
            // have caught it (PARSE-6).
            if (ib >> 5 !== 2 || (ib & 0x1f) === 31) throw new CborError('bad indefinite byte string chunk')
            parts.push(readByteString(c, ib & 0x1f))
        }
        return concat(parts)
    }
    const n = Number(readUint(c, minor))
    // copy out so the result is independent of the source buffer lifetime
    return new Uint8Array(c.bytes(n))
}

function readTextString (c: Cursor, minor: number): string {
    if (isIndefinite(minor)) {
        let s = ''
        for (;;) {
            const ib = c.byte()
            if (ib === 0xff) break
            // RFC 8949 §3.2.3, mirror of readByteString: definite-length chunks
            // only; a nested indefinite marker is ill-formed and would recurse
            // unbounded past the depth cap (PARSE-6).
            if (ib >> 5 !== 3 || (ib & 0x1f) === 31) throw new CborError('bad indefinite text string chunk')
            s += readTextString(c, ib & 0x1f)
        }
        return s
    }
    const n = Number(readUint(c, minor))
    return utf8Decode(c.bytes(n))
}

function readArray (c: Cursor, minor: number): unknown[] {
    const out: unknown[] = []
    if (isIndefinite(minor)) {
        for (;;) {
            const item = readItemOrBreak(c)
            if (item === BREAK) break
            out.push(item)
        }
        return out
    }
    const n = Number(readUint(c, minor))
    for (let i = 0; i < n; i++) out.push(readItem(c))
    return out
}

function readMap (c: Cursor, minor: number): CborMap {
    const entries: Array<[unknown, unknown]> = []
    if (isIndefinite(minor)) {
        for (;;) {
            const key = readItemOrBreak(c)
            if (key === BREAK) break
            entries.push([key, readItem(c)])
        }
    } else {
        const n = Number(readUint(c, minor))
        for (let i = 0; i < n; i++) entries.push([readItem(c), readItem(c)])
    }
    // Well-formedness: reject duplicate keys (RFC 8949 App. C) rather than silently
    // taking last-wins, which would let a map be reinterpreted after hashing.
    const seen = new Set<string>()
    for (const [k] of entries) {
        const kk = typeof k === 'string' ? `s:${k}`
            : (typeof k === 'number' || typeof k === 'bigint') ? `n:${k.toString()}`
            : k instanceof Uint8Array ? `b:${Array.from(k).join(',')}`
            : null
        if (kk !== null) {
            if (seen.has(kk)) throw new CborError('duplicate map key')
            seen.add(kk)
        }
    }
    // String-keyed → plain object (claims/assertions); otherwise Map (COSE headers).
    if (entries.every(([k]) => typeof k === 'string')) {
        const obj: Record<string, unknown> = {}
        for (const [k, v] of entries) obj[k as string] = v
        return obj
    }
    return new Map(entries)
}

function readSimple (c: Cursor, minor: number): unknown {
    switch (minor) {
        case 20: return false
        case 21: return true
        case 22: return null
        case 23: return undefined
        case 24: return c.byte() // simple value
        case 25: return readFloat16(c)
        case 26: return c.dv.getFloat32(advance(c, 4), false)
        case 27: return c.dv.getFloat64(advance(c, 8), false)
        case 31: return BREAK
        default: return minor // small simple value
    }
}

function readItemOrBreak (c: Cursor): unknown {
    // peek for the 0xff break marker
    if (c.u8[c.pos] === 0xff) { c.pos++; return BREAK }
    return readItem(c)
}

function advance (c: Cursor, n: number): number {
    const at = c.pos
    c.pos += n
    if (c.pos > c.u8.length) throw new CborError('unexpected end of input')
    return at
}

function readFloat16 (c: Cursor): number {
    const half = c.uint(2)
    const exp = (half >> 10) & 0x1f
    const mant = half & 0x3ff
    const sign = half & 0x8000 ? -1 : 1
    if (exp === 0) return sign * mant * 2 ** -24
    if (exp === 0x1f) return mant ? NaN : sign * Infinity
    return sign * (mant + 1024) * 2 ** (exp - 25)
}

function concat (parts: Uint8Array[]): Uint8Array {
    let len = 0
    for (const p of parts) len += p.length
    const out = new Uint8Array(len)
    let off = 0
    for (const p of parts) { out.set(p, off); off += p.length }
    return out
}

const textDecoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null
function utf8Decode (bytes: Uint8Array): string {
    if (textDecoder) return textDecoder.decode(bytes)
    // minimal fallback
    let s = ''
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
    return s
}
